import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRebaseClient } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";
import type { EntityCustomViewParams, FormContext } from "@rebasepro/cms-types";
import {
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    Separator as RebaseSeparator,
    Typography as RebaseTypography,
    cls,
    defaultBorderMixin,
    iconSize
} from "@rebasepro/ui";
import {
    FileText as RebaseFileTextIcon,
    Link2 as RebaseLinkIcon,
    Mail as RebaseMailIcon,
    MapPin as RebaseMapPinIcon,
    Phone as RebasePhoneIcon
} from "lucide-react";

import { EmailComposer } from "./EmailComposer";
import { useAnnouncedSnackbar } from "./_workspace";
import { callFunction, type MailboxStatus } from "./_email_shared";
import { sinceLabel } from "./_prospects_shared";
import { usePrivateFileOpener } from "./_private_file";
import { ChipList, Fact, Notes, RecentThreads, Tags, coverRootCls, textList } from "./_cover";
import { credentialLabel } from "../taxonomy/credentials";
import { EntityLink } from "./_links";
import { TalentPresentations } from "./PresentCandidates";
import { PersonAvatar } from "./_avatar";
import { LEVEL_COLORS, describeSalary, summarizeTalent } from "./_talent_taxonomy";
import {
    APPLICATION_ACTIONS,
    APPLICATION_COLOR,
    APPLICATION_LABEL,
    TALENT_STAGE_COLOR,
    TALENT_STAGE_LABEL,
    TALENT_STATUS_COLOR,
    TALENT_TABS,
    buildCandidates,
    talentStageHint,
    talentStageOf,
    talentStatusLabel,
    type ApplicationRow,
    type ApplicationStatus,
    type TalentRow,
    type TalentStatus
} from "./_talents_shared";

const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const Separator = RebaseSeparator as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const FileTextIcon = RebaseFileTextIcon as React.ComponentType<any>;
const LinkIcon = RebaseLinkIcon as React.ComponentType<any>;
const MailIcon = RebaseMailIcon as React.ComponentType<any>;
const MapPinIcon = RebaseMapPinIcon as React.ComponentType<any>;
const PhoneIcon = RebasePhoneIcon as React.ComponentType<any>;

/**
 * A candidate's front cover: everything you need to decide what to do about
 * this person, on one screen, without opening anything.
 *
 * It exists because the same person was being described twice, differently, in
 * the two places anyone looks at them. The queue's detail pane knew their name,
 * their email and their applications; their phone, their CV, the areas they
 * graded, the years they claim and — the one that hurt — every email we have
 * ever exchanged with them were only reachable by leaving the queue for the
 * record. So the queue answered "who is waiting" and then refused to answer
 * "who is this", and the record answered "who is this" as forty form fields.
 *
 * One component, mounted in both:
 *
 *  - as the detail pane of the "Cola" view mode, where it replaces the pane
 *    that was written inline in `TalentsQueue`;
 *  - as the `resumen` entity view on `talents`, where `defaultSelectedView`
 *    makes it the tab that opens — the cover of the record, with the form,
 *    the questionnaire and the correspondence as the tabs beside it.
 *
 * That is the whole point of the file: the two surfaces cannot drift, because
 * there is only one of them.
 *
 * ── What it reads, and from where ───────────────────────────────────
 *
 * Everything on screen is either on the row already or one query away. Nothing
 * here derives an opinion — no scoring, no ranking of a person against a
 * vacancy — and the only person↔job links it draws are applications the
 * candidate made themselves, which is the rule the deleted matching feature
 * broke and this repo keeps.
 *
 * The host may hand it work it has already done: the queue holds every
 * application and every job title in memory, so it passes them and this fetches
 * nothing. The entity tab passes neither and fetches both, scoped to one person.
 */

/**
 * Enum labels this screen prints, kept beside the fields that print them.
 *
 * `QuestionnairePanel` resolves these from the collection itself, which is the
 * better rule and the one to follow if this list grows: sixty Spanish labels
 * copied out of the wizard are sixty chances to drift from what the candidate
 * saw. Four are here because the cover reads exactly four enums and importing
 * the collection to resolve them costs a module the queue does not otherwise
 * need.
 */
const LANGUAGE_LEVEL: Record<string, string> = {
    basic: "Básico",
    intermediate: "Intermedio",
    advanced: "Avanzado",
    native: "Nativo"
};

const WORK_MODEL_LABEL: Record<string, string> = {
    on_site: "Presencial",
    hybrid: "Híbrido",
    remote: "100% Remoto",
    any: "Cualquiera"
};

const TRAVEL_LABEL: Record<string, string> = {
    none: "Nula",
    occasional: "Ocasional",
    frequent: "Frecuente"
};

const RELOCATION_LABEL: Record<string, string> = {
    yes: "Sí",
    no: "No",
    conditional: "Solo bajo ciertas condiciones"
};

/**
 * How this person got into the database, said in full.
 *
 * "se registró" sat next to "registrado hace 1 mes" in the same line and the
 * two read as one stutter. And `email` was missing outright (0051 added it),
 * so a candidate created from a conversation showed the raw column value.
 */
const ORIGIN_LABEL: Record<string, string> = {
    signup: "llegó por el formulario de la web",
    cv_import: "lo creamos importando su CV",
    manual: "lo creamos a mano",
    email: "llegó escribiendo un correo"
};

/**
 * A day, for the two lines that report what we sent rather than what they said.
 *
 * `sinceLabel` is the right shape everywhere else on this cover — a profile's
 * age is felt in months, not dates — but "le avisamos hace 34 días" asks the
 * reader to do arithmetic to answer "was that before or after she confirmed".
 * A date does not.
 */
function dayLabel(iso: string): string {
    const when = new Date(iso);
    if (Number.isNaN(when.getTime())) return "";
    return when.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
}

export interface TalentOverviewProps {
    /** The candidate, as a whole row: `entity.values` or a row from the queue. */
    row: TalentRow;
    /**
     * Their applications, when the host already has them. Left out, the cover
     * reads the two columns that link an application to a person itself.
     */
    applications?: ApplicationRow[];
    /** Names a vacancy, when the host already holds the board. */
    jobTitle?: (jobId: string | null | undefined) => string;
    /**
     * The mailbox's own report, when the host already asked for it. `undefined`
     * means "nobody asked" and the cover asks; `null` means "asked and it did
     * not answer", which is not the same thing and must not trigger a retry.
     */
    mailbox?: MailboxStatus | null;
    /**
     * The form's context, when this is mounted as an entity view.
     *
     * Present, `Estado` writes through the form rather than behind its back.
     * Behind its back is a real bug and not a stylistic one: the form holds
     * `lead_status` in its own state, so a direct write to the table would be
     * silently reverted by the next Save the operator pressed on any other
     * field.
     */
    formContext?: FormContext<any>;
    /** The status this view just wrote, for a host that renders the row too. */
    onStatusChange?: (id: string, status: TalentStatus) => void;
    /** An application moved. The host that owns the list refetches. */
    onApplicationsChanged?: () => void;
    /** Host-specific controls for the identity block — "ficha completa". */
    headerExtra?: React.ReactNode;
    className?: string;
}

export function TalentOverview({
    row,
    applications,
    jobTitle,
    mailbox,
    formContext,
    onStatusChange,
    onApplicationsChanged,
    headerExtra,
    className
}: TalentOverviewProps) {
    const client = useRebaseClient<RebaseClient>();
    const { snackbar, liveRegion } = useAnnouncedSnackbar();
    const openPrivateFile = usePrivateFileOpener();
    const data = (client as any)?.data;
    const talentId = String(row.id);

    const [working, setWorking] = useState(false);
    const [composing, setComposing] = useState(false);
    /** Bumped after a send, so "Correo" further down lists what was just sent. */
    const [sent, setSent] = useState(0);
    /**
     * The statuses this view has just written, laid over the row until whoever
     * owns it refetches. Same overlay the queue keeps over its list and for the
     * same reason: pressing a button and watching the chip beside it keep the
     * old value is how a screen stops being believed.
     */
    const [statusOverlay, setStatusOverlay] = useState<TalentStatus | null>(null);
    const [applicationOverlay, setApplicationOverlay] = useState<Record<string, ApplicationStatus>>({});

    const [ownApplications, setOwnApplications] = useState<ApplicationRow[] | null>(null);
    const [ownTitles, setOwnTitles] = useState<Map<string, string>>(new Map());
    const [ownMailbox, setOwnMailbox] = useState<MailboxStatus | null>(null);

    // A cover left mounted across a change of person — the queue keeps this
    // component alive and swaps `row` — must not carry the previous one's
    // half-written message, their status overlay, or their candidacies into the
    // next.
    useEffect(() => {
        setComposing(false);
        setStatusOverlay(null);
        setApplicationOverlay({});
        setOwnApplications(null);
    }, [talentId]);

    /**
     * Whose applications the last request was for.
     *
     * Same guard the mailbox panel keeps, for the same reason: click through
     * three candidates quickly and the answers can land in any order, so without
     * this the third person's list is overwritten by the first person's — and
     * `buildCandidates` filters those rows out by id, so what is left on screen
     * is not somebody else's candidacies but *none*, under a person who has some.
     */
    const wantedApplications = useRef<string | null>(null);

    /**
     * The applications, when the host did not bring them.
     *
     * Two queries, because an application points at a person by `talent_id`
     * when it has one and by `auth_user_id` when it does not — 0030 backfilled
     * the first and older rows still carry only the second. Asking for one of
     * them loses candidacies silently, which here reads as "no se ha postulado
     * a nada" printed over somebody who did.
     */
    const loadApplications = useCallback(async () => {
        if (!data || applications) return;
        wantedApplications.current = talentId;
        try {
            const queries = [
                data.collection("talent-applications").find({
                    where: { talent_id: ["==", talentId] },
                    limit: 100
                })
            ];
            if (row.auth_user_id) {
                queries.push(data.collection("talent-applications").find({
                    where: { auth_user_id: ["==", row.auth_user_id] },
                    limit: 100
                }));
            }
            const pages = await Promise.all(queries);
            if (wantedApplications.current !== talentId) return;
            const seen = new Map<string, ApplicationRow>();
            for (const page of pages) {
                for (const application of ((page?.data ?? []) as ApplicationRow[])) {
                    if (application?.id) seen.set(String(application.id), application);
                }
            }
            setOwnApplications([...seen.values()]);
        } catch {
            if (wantedApplications.current !== talentId) return;
            // An unreadable table and an empty one must not render the same, so
            // this stays `null` and the section below stays quiet rather than
            // announcing that nobody applied.
            setOwnApplications(null);
        }
    }, [data, applications, talentId, row.auth_user_id]);

    useEffect(() => { void loadApplications(); }, [loadApplications]);

    const applicationRows = useMemo(() => {
        const source = applications ?? ownApplications ?? [];
        return source.map(application => ({
            ...application,
            ...(applicationOverlay[String(application.id)]
                ? { status: applicationOverlay[String(application.id)] }
                : {})
        }));
    }, [applications, ownApplications, applicationOverlay]);

    const candidate = useMemo(
        () => buildCandidates(
            [{ ...row, id: talentId, ...(statusOverlay ? { lead_status: statusOverlay } : {}) }],
            applicationRows
        )[0],
        [row, talentId, statusOverlay, applicationRows]
    );

    /** The vacancies these applications name, when the host holds no board. */
    useEffect(() => {
        if (!data || jobTitle) return;
        const ids = [...new Set(candidate.applications
            .map(a => a.job_id)
            .filter(Boolean)
            .map(id => String(id)))];
        if (ids.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const page = await data.collection("jobs").find({
                    where: { id: ["in", ids] },
                    limit: ids.length
                });
                if (cancelled) return;
                const titles = new Map<string, string>();
                for (const job of ((page?.data ?? []) as { id: string; title?: string | null }[])) {
                    if (job?.id) titles.set(String(job.id), (job.title ?? "").trim() || "(vacante sin título)");
                }
                setOwnTitles(titles);
            } catch {
                // Named "(vacante desconocida)" below, which is honest.
            }
        })();
        return () => { cancelled = true; };
    }, [data, jobTitle, candidate.applications]);

    /**
     * Whether the candidacies are actually known yet.
     *
     * `candidate.applications` is an array either way, so "still reading" and
     * "there are none" render identically — and the empty state here is not a
     * dash, it is a paragraph asserting that this person never applied to
     * anything. Printed for the half second before the query lands, over
     * somebody with three candidacies, on a screen whose whole job is to say
     * what to do next.
     */
    const applicationsKnown = applications !== undefined || ownApplications !== null;

    const nameOfJob = useCallback(
        (jobId: string | null | undefined) => jobTitle
            ? jobTitle(jobId)
            : (jobId ? ownTitles.get(String(jobId)) : undefined) ?? "(vacante desconocida)",
        [jobTitle, ownTitles]
    );

    useEffect(() => {
        if (mailbox !== undefined) return;
        callFunction<MailboxStatus>(client, "sync-inbox")
            .then(setOwnMailbox)
            .catch(() => setOwnMailbox(null));
    }, [client, mailbox]);

    const usedMailbox = mailbox !== undefined ? mailbox : ownMailbox;

    const setTalentStatus = useCallback(async (status: TalentStatus) => {
        if (!formContext && !data) return;
        setWorking(true);
        try {
            if (formContext && !formContext.readOnly) {
                // Through the form, and with the value passed explicitly:
                // `setFieldValue` lands on the next render and `save` reads the
                // values it is given, so handing it the object avoids saving the
                // status the operator just replaced.
                formContext.setFieldValue("lead_status", status);
                await Promise.resolve(formContext.save({
                    ...(formContext.values as Record<string, unknown>),
                    lead_status: status
                } as any));
            } else {
                await data.collection("talents").update(talentId, { lead_status: status });
            }
            setStatusOverlay(status);
            onStatusChange?.(talentId, status);
            snackbar.open({ type: "success", message: `${candidate.name} → ${talentStatusLabel(status)}` });
        } catch (err: any) {
            snackbar.open({ type: "error", message: err?.message || "No se pudo cambiar el estado" });
        } finally {
            setWorking(false);
        }
    }, [data, formContext, talentId, candidate.name, onStatusChange, snackbar]);

    /**
     * Tags, written the same two ways the status is: through the form when
     * the cover sits inside one, straight to the row from the queue.
     */
    const [tagsOverlay, setTagsOverlay] = useState<string[] | null>(null);
    useEffect(() => { setTagsOverlay(null); }, [talentId]);
    const tags = tagsOverlay ?? ((row.tags ?? []) as string[]);
    const setTags = useCallback(async (next: string[]) => {
        if (!formContext && !data) return;
        try {
            if (formContext && !formContext.readOnly) {
                formContext.setFieldValue("tags", next);
                await Promise.resolve(formContext.save({
                    ...(formContext.values as Record<string, unknown>),
                    tags: next
                } as any));
            } else {
                await data.collection("talents").update(talentId, { tags: next });
            }
            setTagsOverlay(next);
        } catch (err: any) {
            snackbar.open({ type: "error", message: err?.message || "No se pudieron guardar las etiquetas" });
        }
    }, [data, formContext, talentId, snackbar]);

    const setApplicationStatus = useCallback(async (application: ApplicationRow, status: ApplicationStatus) => {
        if (!data) return;
        setWorking(true);
        try {
            await data.collection("talent-applications").update(application.id, { status });
            setApplicationOverlay(prev => ({ ...prev, [String(application.id)]: status }));
            onApplicationsChanged?.();
            snackbar.open({ type: "success", message: `Candidatura → ${APPLICATION_LABEL[status]}` });
        } catch (err: any) {
            snackbar.open({ type: "error", message: err?.message || "No se pudo cambiar la candidatura" });
        } finally {
            setWorking(false);
        }
    }, [data, onApplicationsChanged, snackbar]);

    const stage = talentStageOf(candidate);
    const status = candidate.status;
    const profile = useMemo(() => summarizeTalent(row), [row]);
    const questionnaire = (row.questionnaire ?? {}) as Record<string, any>;

    /**
     * The areas worth reading, strongest first.
     *
     * `summarizeTalent` already collapses the two vocabularies and drops the
     * `none` answers — an area graded "sin experiencia" is the absence of a
     * claim, and printing seventeen of them buries the three that matter.
     */
    const areas = useMemo(
        () => profile.areas.filter(a => a.level !== "none").slice(0, 8),
        [profile.areas]
    );

    const languages = useMemo(() => {
        const list = Array.isArray(questionnaire.languages) ? questionnaire.languages : [];
        return list
            .map((l: any) => {
                const language = (l?.language ?? "").toString().trim();
                if (!language) return null;
                const level = l?.level ? LANGUAGE_LEVEL[String(l.level)] ?? String(l.level) : null;
                return level ? `${language} · ${level}` : language;
            })
            .filter(Boolean) as string[];
    }, [questionnaire.languages]);

    // The cover shows the monthly reading and where it came from, never the
    // raw pair — the same number was a month for half the base and a year for
    // the other half. See `describeSalary`.
    const salary = useMemo(() => describeSalary(questionnaire), [questionnaire]);

    const workModel = useMemo(() => {
        const list = Array.isArray(questionnaire.preferred_work_model) ? questionnaire.preferred_work_model : [];
        return list.map((v: unknown) => WORK_MODEL_LABEL[String(v)] ?? String(v));
    }, [questionnaire.preferred_work_model]);

    // Both canonical where we have it — see `textList` — and both printed
    // through the catalog, because the canonical form is an id: the cover was
    // showing "iso_14001" to a recruiter, which is the database talking.
    // `credentialLabel` hands back anything it does not know unchanged, so a
    // certification the catalog never heard of still shows as the person
    // wrote it.
    const certifications = textList(row.certifications_canon, questionnaire.certifications).map(credentialLabel);
    const skills = textList(row.skills_canon, questionnaire.skills).map(credentialLabel);

    const composerTarget = useMemo(() => ({
        to: candidate.email ? [candidate.email] : [],
        subject: "",
        threadId: null,
        // A person opened this candidate and pressed Escribirle. `manual` is a
        // stronger statement about what the conversation is about than any guess
        // the poller could make from an address, and ingest never overwrites it.
        link: { entity_type: "talent" as const, entity_id: talentId },
        draftContext: {
            entity_type: "talent" as const,
            entity_id: talentId,
            tone: "candidato" as const
        },
        // For the templates: "Hola {{nombre}}," becomes their first name.
        recipientName: candidate.name
    }), [candidate.email, candidate.name, talentId]);

    const meta = [
        row.created_at ? `en la base desde ${sinceLabel(row.created_at)}` : null,
        row.origin ? ORIGIN_LABEL[row.origin] ?? row.origin : null,
        row.questionnaire_completed_at
            ? `cuestionario ${sinceLabel(row.questionnaire_completed_at)}`
            : "cuestionario sin terminar",
        row.profile_confirmed_at ? `confirmó sus datos ${sinceLabel(row.profile_confirmed_at)}` : null,
        row.linkedin_linked_at ? "LinkedIn conectado" : null
    ].filter(Boolean).join(" · ");

    /**
     * What we have written to them, under what they told us (drizzle/0057).
     *
     * A separate line and not another `·` in `meta`, because it is the other
     * voice: everything above is the candidate's own record, and this is ours.
     * Dated rather than relative — `sinceLabel` is right for "confirmó hace
     * seis meses", and wrong here, where the question is "have we already
     * written to her" and the answer is a day.
     */
    const outreach = [
        row.profile_nudge_sent_at ? `Le avisamos el ${dayLabel(row.profile_nudge_sent_at)}` : null,
        row.profile_nudge_reminded_at ? `Recordatorio el ${dayLabel(row.profile_nudge_reminded_at)}` : null
    ].filter(Boolean).join(" · ");

    return (
        <div className={cls(coverRootCls, className)}>
            {liveRegion}

            {/* ── Who this is ─────────────────────────────────────── */}
            <div className="flex items-start gap-4">
                {/* Always drawn, photo or not.

                    This was `{row.photo_url && <img src={row.photo_url}>}`,
                    which is two faults in one line: `photo_url` holds a storage
                    key and not a URL, so the src 404'd for all 50 candidates who
                    have a photo — and the 350 who have none got nothing at all,
                    so the cover's header shifted left depending on the person.
                    `PersonAvatar` exchanges the key for a download URL and falls
                    back to their initials. */}
                <PersonAvatar name={candidate.name} photoKey={row.photo_url} size={64} />
                <div className="flex flex-col gap-2 min-w-0 grow">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Typography variant="h5" component="h2" className="!mb-0">{candidate.name}</Typography>
                        <Chip size="small" colorScheme={TALENT_STATUS_COLOR[status]}>
                            {talentStatusLabel(status)}
                        </Chip>
                        {row.profile_inflated && (
                            // Said plainly, where a count of "advanced" areas
                            // would read as a strength.
                            <Chip size="small" colorScheme="orangeLighter">se declara avanzado en casi todo</Chip>
                        )}
                        <div className="grow" />
                        {headerExtra}
                    </div>

                    <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                        {candidate.email && (
                            <span className="inline-flex items-center gap-1.5 text-sm">
                                <MailIcon size={iconSize.smallest} className="opacity-60" />
                                {candidate.email}
                            </span>
                        )}
                        {row.phone && (
                            <span className="inline-flex items-center gap-1.5 text-sm">
                                <PhoneIcon size={iconSize.smallest} className="opacity-60" />
                                {row.phone}
                            </span>
                        )}
                        {(row.location || profile.location) && (
                            <span className="inline-flex items-center gap-1.5 text-sm">
                                <MapPinIcon size={iconSize.smallest} className="opacity-60" />
                                {row.location || profile.location}
                            </span>
                        )}
                        {/* `cv_url` is a storage key, never a URL: as an href it
                            was a relative path that 404ed. Exchanged for a
                            signed link on click — see `_private_file.tsx`. */}
                        {row.cv_url && (
                            <button
                                type="button"
                                onClick={async () => {
                                    const outcome = await openPrivateFile(row.cv_url, "el CV");
                                    if (!outcome.ok) snackbar.open({ type: "error", message: outcome.message });
                                }}
                                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                            >
                                <FileTextIcon size={iconSize.smallest} /> CV
                            </button>
                        )}
                        {row.linkedin_url && (
                            <a href={row.linkedin_url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                                <LinkIcon size={iconSize.smallest} /> LinkedIn
                            </a>
                        )}
                    </div>

                    {meta && <Typography variant="caption" color="secondary">{meta}</Typography>}
                    {outreach && <Typography variant="caption" color="secondary">{outreach}</Typography>}
                    {row.email_bounced_at && (
                        // Said where the address is, because it is a fact about
                        // the address: everything automatic stops here, and the
                        // email printed two lines above no longer reaches
                        // anybody. Writing by hand is still worth trying — a
                        // person can notice a typo, which is why this is a
                        // sentence and not a disabled button.
                        <Typography variant="caption" color="error">
                            Su email rebotó el {dayLabel(row.email_bounced_at)} — no se le envía nada automático
                        </Typography>
                    )}
                </div>
            </div>

            {/* ── What to do about them ───────────────────────────── */}
            {stage !== "done" && (
                <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Typography variant="micro" color="secondary">Lo siguiente</Typography>
                        <Chip size="small" colorScheme={TALENT_STAGE_COLOR[stage]}>
                            {TALENT_STAGE_LABEL[stage]}
                        </Chip>
                    </div>
                    <Typography variant="body2">{talentStageHint(candidate, nameOfJob)}</Typography>
                    <div className="flex gap-2 flex-wrap items-center">
                        {candidate.email && (
                            <Button size="small" disabled={working} onClick={() => setComposing(v => !v)}>
                                {composing ? "Cerrar el mensaje" : "Escribirle"}
                            </Button>
                        )}
                        {/* "Marcar como revisado" was here, for the `review`
                            stage that no longer exists: a click that moved a
                            status column and nothing else, offered 365 times. */}
                        {!candidate.email && (
                            <Typography variant="body2" color="secondary">
                                No dejó email, así que desde aquí no se le puede escribir.
                            </Typography>
                        )}
                    </div>
                </Card>
            )}

            {composing && candidate.email && (
                <EmailComposer
                    client={client}
                    compact
                    canSend={usedMailbox?.sending !== false}
                    target={composerTarget}
                    onSent={async () => {
                        setComposing(false);
                        setSent(n => n + 1);
                        // Writing to somebody is contacting them, and leaving
                        // them in "sin revisar" afterwards is how a queue asks
                        // for work already done.
                        if (status === "new") await setTalentStatus("contacted");
                        snackbar.open({ type: "success", message: "Enviado" });
                    }}
                    onCancel={() => setComposing(false)}
                />
            )}

            <Separator />

            {/* ── Who they are professionally ─────────────────────── */}
            <div className="flex flex-col gap-3">
                <Typography variant="subtitle2" component="h3">Perfil</Typography>

                {!row.questionnaire_completed_at && (
                    <Typography variant="body2" color="secondary">
                        No terminó el cuestionario, así que lo que falta aquí es una pregunta sin
                        responder y no un perfil pobre.
                    </Typography>
                )}

                <div className="grid gap-x-6 gap-y-3 grid-cols-1 sm:grid-cols-2">
                    <Fact label="Experiencia">{profile.experienceLabel}</Fact>
                    <Fact label="Idiomas">{languages.length ? languages.join(" · ") : null}</Fact>
                    <Fact label="Modalidad">{workModel.length ? workModel.join(" · ") : null}</Fact>
                    <Fact label="Expectativa salarial">
                        {salary && (
                            <>
                                <span className={salary.source === "unresolved" ? "opacity-70" : undefined}>{salary.text}</span>
                                {salary.note && (
                                    <Typography variant="caption" color="secondary" className="block mt-0.5">
                                        {salary.source === "inferred" ? "≈ " : ""}{salary.note}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Fact>
                    <Fact label="Disponibilidad para viajar">
                        {questionnaire.travel_availability ? TRAVEL_LABEL[String(questionnaire.travel_availability)] ?? String(questionnaire.travel_availability) : null}
                    </Fact>
                    <Fact label="Reubicación">
                        {questionnaire.relocation_available ? RELOCATION_LABEL[String(questionnaire.relocation_available)] ?? String(questionnaire.relocation_available) : null}
                    </Fact>
                </div>

                {areas.length > 0 && (
                    <Fact label="Áreas de sostenibilidad">
                        <div className="flex flex-wrap gap-1 mt-0.5">
                            {areas.map(area => (
                                <Chip key={area.key} size="small" colorScheme={LEVEL_COLORS[area.level] ?? "grayLighter"}>
                                    {area.label} · {area.levelLabel}
                                </Chip>
                            ))}
                        </div>
                    </Fact>
                )}

                {/* Guarded out here rather than inside `Fact`: a component that
                    renders nothing is still a child, so `Fact` sees one and
                    prints its label over an empty row. */}
                {certifications.length > 0 && (
                    <Fact label="Certificaciones"><ChipList values={certifications} /></Fact>
                )}
                {skills.length > 0 && (
                    <Fact label="Habilidades"><ChipList values={skills} /></Fact>
                )}

                {row.professional_objective && (
                    <Fact label="Lo que busca, en sus palabras">
                        <span className="whitespace-pre-wrap break-words">{row.professional_objective}</span>
                    </Fact>
                )}
            </div>

            <Separator />

            {/* ── What they applied to ────────────────────────────── */}
            <div className="flex flex-col gap-3">
                <Typography variant="subtitle2" component="h3">
                    Candidaturas ({candidate.applications.length})
                </Typography>
                {applicationsKnown && candidate.applications.length === 0 && (
                    <Typography variant="body2" color="secondary">
                        No se ha postulado a nada. Sólo 4 de las vacantes del tablón admiten
                        candidatura dentro de SustenTalent; en el resto el botón lleva fuera,
                        así que esto es lo normal y no dice nada malo del perfil.
                    </Typography>
                )}
                {candidate.applications.map(application => {
                    const applicationStatus = (application.status ?? "applied") as ApplicationStatus;
                    return (
                        <Card key={application.id} className="p-3 flex flex-col gap-2">
                            <div className="flex items-start gap-2 flex-wrap">
                                <Typography variant="body2" className="grow font-medium">
                                    <EntityLink kind="job" id={application.job_id}>{nameOfJob(application.job_id)}</EntityLink>
                                </Typography>
                                <Chip size="small" colorScheme={APPLICATION_COLOR[applicationStatus]}>
                                    {APPLICATION_LABEL[applicationStatus]}
                                </Chip>
                            </div>
                            <Typography variant="body2" color="secondary">
                                se postuló {sinceLabel(application.created_at)}
                            </Typography>
                            <div className="flex gap-2 flex-wrap">
                                {APPLICATION_ACTIONS.filter(next => next !== applicationStatus).map(next => (
                                    <Button
                                        key={next}
                                        size="small"
                                        variant="outlined"
                                        disabled={working}
                                        onClick={() => setApplicationStatus(application, next)}
                                    >
                                        {APPLICATION_LABEL[next]}
                                    </Button>
                                ))}
                            </div>
                        </Card>
                    );
                })}
            </div>

            <Separator />

            {/* ── Where the team presented them ───────────────────────
                The other way a person reaches an employer: not an
                application they made, but a presentation somebody on the
                team chose to make (drizzle/0069). Kept apart from
                "Candidaturas" because the two mean different things about
                who decided. */}
            <TalentPresentations talentId={talentId} talentName={candidate.name} />

            <Separator />

            {/* ── What the team wrote down ────────────────────────── */}
            <Tags values={tags} onChange={setTags} disabled={working} />
            <Notes kind="talent" entityId={talentId} />

            <Separator />

            {/* ── What we have said to them ───────────────────────── */}
            <RecentThreads
                kind="talent"
                entityId={talentId}
                emptyLabel="Todavía no hemos hablado con esta persona."
                refresh={sent}
            />

            <Separator />

            {/* ── The funnel, last: it is where they are, not what to do ── */}
            <div className="flex flex-col gap-3">
                <Typography variant="subtitle2" component="h3">Estado</Typography>
                <div className="flex flex-wrap gap-2">
                    {TALENT_TABS.map(t => (
                        <Button
                            key={t.id}
                            size="small"
                            variant={status === t.id ? "filled" : "outlined"}
                            disabled={working || status === t.id}
                            onClick={() => setTalentStatus(t.id)}
                        >
                            {t.label}
                        </Button>
                    ))}
                </div>
            </div>
        </div>
    );
}

/**
 * The cover, as the entity's own first tab.
 *
 * Registered on `talents` from `frontend/src/App.tsx` and named by
 * `defaultSelectedView` in `collections/talents.ts`, so opening a candidate —
 * from the queue, the list or a link — lands on this and not on the form. The
 * form is still one tab away, and it is where you go to *correct* the record;
 * this is where you go to read it.
 *
 * `modifiedValues` before `entity.values`: with the form open beside it, a
 * location typed and not yet saved should show here too. A cover contradicting
 * the field the operator is editing reads as a stale screen.
 */
export function TalentOverviewTab({ entity, modifiedValues, formContext }: EntityCustomViewParams) {
    // Nothing to cover yet, and nothing that would work: applications and
    // conversations are filed against an id that does not exist until the first
    // save, so this would render three empty sections and a status button that
    // writes to no row.
    if (!entity?.id) {
        return (
            <div className="p-6">
                <Typography variant="body1" color="secondary">
                    Guarda la ficha primero. Esta portada resume lo que ya está en la base —
                    sus candidaturas y su correo se archivan contra un registro que existe.
                </Typography>
            </div>
        );
    }

    const values = (modifiedValues ?? entity.values ?? {}) as Record<string, unknown>;
    return (
        <TalentOverview
            row={{ ...values, id: String(entity.id) } as TalentRow}
            formContext={formContext as FormContext<any>}
        />
    );
}

export default TalentOverview;
